import { URLS, FONTS } from '../../config/app-config';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    CircularProgress,
    DialogContentText,
    MenuItem,
    Pagination,
    TextField,
    Typography,
} from '@mui/material';
import FilterPanel from '../filters/FilterPanel';
import GlossaryDialog from '../dialogs/GlossaryDialog';
import SnakeGame from '../other/SnakeGame';
import SearchResult from './SearchResult';
import buildNestedFilters from '../../utils/buildNestedFilters';
import { getHighlightedPreview, resultMatchesFilter } from '../../utils/searchPreviewUtils';

import { secureFetch } from '../../utils/secureFetch';

export async function getElasticSuggestions({ query, release }, signal) {
    const params = release.toSearchParams({ q: String(query || '').trim() });
    const response = await secureFetch(`${URLS.apiBase.replace(/\/$/, '')}/search-suggestion?${params}`, { signal });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
}

const ElasticSearch = ({
    request,
    submittedQuery,
    onCloseSearch,
    selectedRelease,
    tocData = [],
    offlineMode = false,
    handleArticleSelect,
    setSelectedRelease,
    fetchArticle,
}) => {
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const saved = parseInt(localStorage.getItem('searchItemsPerPage') ?? '10', 10);
        return Number.isFinite(saved) ? saved : 10;
    });
    const [glossary, setGlossary] = useState({ open: false, html: '', title: '' });
    const searchController = useRef(null);
    const searchSequence = useRef(0);
    const lastRequestId = useRef(null);

    useEffect(() => () => searchController.current?.abort(), []);

    const performSearch = useCallback((query) => {
        if (!query) return;
        searchController.current?.abort();
        const controller = new AbortController();
        searchController.current = controller;
        const sequence = ++searchSequence.current;
        const params = selectedRelease.toSearchParams({ q: query });

        setLoading(true);
        secureFetch(`${URLS.apiBase.replace(/\/$/, '')}/search?${params}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((data) => {
                if (sequence !== searchSequence.current) return;
                setSearchResults(Array.isArray(data) ? data : data?.results || []);
            })
            .catch((error) => {
                if (error?.name === 'AbortError' || sequence !== searchSequence.current) return;
                console.error('Elastic search failed:', error);
                setSearchResults([]);
            })
            .finally(() => {
                if (sequence === searchSequence.current) setLoading(false);
            });
    }, [selectedRelease]);

    useEffect(() => {
        if (!request?.query) return;
        if (lastRequestId.current !== request.id) {
            lastRequestId.current = request.id;
            setSelectedFilters([]);
            setPage(1);
        }
        performSearch(request.query);
    }, [performSearch, request]);

    useEffect(() => {
        localStorage.setItem('searchItemsPerPage', String(itemsPerPage));
        setPage(1);
    }, [itemsPerPage]);

    const filterTree = useMemo(() => buildNestedFilters(searchResults), [searchResults]);
    const filteredResults = useMemo(() => {
        if (!selectedFilters.length) return searchResults;
        return searchResults.filter((result) => (
            result.metadata?.keywords && selectedFilters.some((filter) => resultMatchesFilter(result, filter))
        ));
    }, [searchResults, selectedFilters]);
    const fallbackSuggestion = filteredResults.find((result) => result.term_suggestion)?.term_suggestion;
    const totalPages = itemsPerPage === 0
        ? 1
        : Math.max(1, Math.ceil(filteredResults.length / itemsPerPage));
    const currentResults = itemsPerPage === 0
        ? filteredResults
        : filteredResults.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const hasResults = filteredResults.length > 0;

    useEffect(() => {
        if (page > totalPages) setPage(totalPages || 1);
    }, [page, totalPages]);

    const openResult = useCallback(async (result) => {
        handleArticleSelect(result._id);
        onCloseSearch();
    }, [handleArticleSelect, onCloseSearch]);

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 1200,
                    height: '100%',
                    minHeight: 0,
                    mx: 'auto',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' },
                    gridTemplateRows: { xs: 'auto minmax(0, 1fr)', md: 'minmax(0, 1fr)' },
                    gap: 3,
                }}
            >
                {hasResults && (
                    <FilterPanel
                        filterTree={filterTree}
                        selectedFilters={selectedFilters}
                        onChangeFilters={setSelectedFilters}
                        selectedRelease={selectedRelease}
                        setSelectedRelease={setSelectedRelease}
                    />
                )}

                <Box
                    sx={{
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: 'auto',
                        pt: 1,
                        pb: 2,
                        gridColumn: { xs: 1, md: hasResults ? 2 : '1 / -1' },
                    }}
                >
                    {!loading && submittedQuery && !hasResults && (
                        <>
                            <Box display="flex" justifyContent="center" mt={5}>
                                <Box component="img" src="/img/sad_dog.png" alt="Sad dog" sx={{ maxWidth: 300, width: '100%' }} />
                            </Box>
                            <Box display="flex" justifyContent="center" my={2}>
                                <Typography sx={{ fontFamily: FONTS.body, fontSize: 20 }}>
                                    No results found for “{submittedQuery}”
                                </Typography>
                            </Box>
                            <SnakeGame />
                        </>
                    )}

                    {!loading && hasResults && (
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                            {!fallbackSuggestion ? (
                                <DialogContentText sx={{ fontFamily: FONTS.body, fontSize: 20 }}>
                                    {filteredResults.length} results for “{submittedQuery}”
                                </DialogContentText>
                            ) : (
                                <Box>
                                    <DialogContentText sx={{ fontFamily: FONTS.body, fontSize: 20 }}>
                                        {filteredResults.length} results for “{fallbackSuggestion}”
                                    </DialogContentText>
                                    <DialogContentText>
                                        No results for “{submittedQuery}”; searched for “{fallbackSuggestion}” instead.
                                    </DialogContentText>
                                </Box>
                            )}
                            <TextField
                                label="Results/Page"
                                size="small"
                                select
                                value={itemsPerPage === 0 ? 'all' : String(itemsPerPage)}
                                onChange={(event) => setItemsPerPage(event.target.value === 'all' ? 0 : Number(event.target.value))}
                                sx={{ width: 150 }}
                            >
                                {[10, 20, 50, 100].map((size) => <MenuItem key={size} value={String(size)}>{size}</MenuItem>)}
                            </TextField>
                        </Box>
                    )}

                    {loading ? (
                        <Box display="flex" justifyContent="center" p={2}><CircularProgress /></Box>
                    ) : hasResults ? (
                        <>
                            <Box component="section" display="flex" flexDirection="column" gap={2}>
                                {currentResults.map((result) => (
                                    <SearchResult
                                        key={result._id}
                                        result={result}
                                        searchQuery={submittedQuery}
                                        onOpenResult={openResult}
                                        getHighlightedPreview={getHighlightedPreview}
                                        selectedRelease={selectedRelease}
                                        tocData={tocData}
                                        offlineMode={offlineMode}
                                        fetchArticle={fetchArticle}
                                    />
                                ))}
                            </Box>
                            {itemsPerPage !== 0 && totalPages > 1 && (
                                <Box display="flex" justifyContent="center" mt={2}>
                                    <Pagination count={totalPages} page={page} onChange={(event, value) => setPage(value)} color="primary" />
                                </Box>
                            )}
                        </>
                    ) : null}
                </Box>
            </Box>

            <GlossaryDialog
                open={glossary.open}
                onClose={() => setGlossary((current) => ({ ...current, open: false }))}
                contentHtml={glossary.html}
                title={glossary.title}
            />
        </>
    );
};

export default ElasticSearch;
